import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, FileVideo, Image, Loader2, RefreshCw, Send, X } from 'lucide-react';
import {
  SUPPORT_REPORT_CATEGORIES,
  SUPPORT_REPORT_LIMITS,
  createSupportReportDraft,
  getMySupportReport,
  listMySupportReports,
  readVideoDuration,
  submitSupportReport,
  uploadSupportReportAttachment,
  validateSupportReportFiles,
} from '@tark/shared/lib';
import type { SupportReportDetail, SupportReportSummary } from '@tark/shared/types';

type Tab = 'new' | 'mine';

export default function ReportProblemPage() {
  const [tab, setTab] = useState<Tab>('new');
  const [category, setCategory] = useState('GENERAL');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [diagnosticsConsent, setDiagnosticsConsent] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedTicket, setSubmittedTicket] = useState<string | null>(null);
  const [reports, setReports] = useState<SupportReportSummary[]>([]);
  const [selected, setSelected] = useState<SupportReportDetail | null>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  const attachmentTotal = useMemo(() => files.reduce((sum, file) => sum + file.size, 0), [files]);

  const loadReports = async () => {
    setLoadingReports(true);
    setError(null);
    try {
      setReports(await listMySupportReports());
    } catch (loadError) {
      setError(messageOf(loadError, 'Your reports could not be loaded.'));
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    if (tab === 'mine') void loadReports();
  }, [tab]);

  const onFiles = (incoming: File[]) => {
    const next = [...files, ...incoming];
    const validation = validateSupportReportFiles(next);
    if (validation) {
      setError(validation);
      return;
    }
    setError(null);
    setFiles(next);
  };

  const submit = async () => {
    if (title.trim().length < 3 || description.trim().length < 10) {
      setError('Add a title and at least 10 characters describing the problem.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const durations = new Map<File, number>();
      for (const file of files.filter((item) => item.type.startsWith('video/'))) {
        const duration = await readVideoDuration(file);
        if (duration > SUPPORT_REPORT_LIMITS.videoDurationMs) {
          throw new Error('The attached video must be 60 seconds or shorter.');
        }
        durations.set(file, duration);
      }

      const ticket = await createSupportReportDraft(idempotencyKey);
      for (const file of files) {
        await uploadSupportReportAttachment(
          ticket,
          file,
          file.type.startsWith('video/') ? 'VIDEO' : 'SCREENSHOT',
          durations.get(file)
        );
        setFiles((current) => current.filter((item) => item !== file));
      }
      await submitSupportReport(ticket, {
        category,
        title: title.trim(),
        description: description.trim(),
        expectedBehavior: expectedBehavior.trim() || undefined,
        stepsToReproduce: stepsToReproduce.trim() || undefined,
        diagnosticsConsent,
        diagnostics: diagnosticsConsent ? {
          platform: 'WEB',
          osVersion: navigator.platform,
          deviceModel: navigator.userAgent.slice(0, 120),
          currentRoute: `${window.location.pathname}${window.location.search}`,
          networkType: navigator.onLine ? 'online' : 'offline',
        } : undefined,
      });
      setSubmittedTicket(ticket);
      setTitle('');
      setDescription('');
      setExpectedBehavior('');
      setStepsToReproduce('');
      setFiles([]);
      setIdempotencyKey(crypto.randomUUID());
    } catch (submitError) {
      setError(messageOf(submitError, 'The report could not be submitted.'));
    } finally {
      setBusy(false);
    }
  };

  const openReport = async (ticket: string) => {
    setError(null);
    try {
      setSelected(await getMySupportReport(ticket));
    } catch (loadError) {
      setError(messageOf(loadError, 'The report details could not be loaded.'));
    }
  };

  return (
    <div className="mx-auto max-w-4xl pb-10">
      <header className="mb-6">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">Help & support</p>
        <h1 className="mt-2 text-3xl font-bold text-text">Report a problem</h1>
        <p className="mt-2 text-text-muted">Tell us what happened. Screenshots and a short recording help us reproduce it.</p>
      </header>

      <div className="mb-6 grid grid-cols-2 rounded-2xl border border-border bg-surface p-1">
        {([['new', 'New report'], ['mine', 'My reports']] as const).map(([value, label]) => (
          <button key={value} type="button" onClick={() => setTab(value)} className={`rounded-xl px-4 py-3 text-sm font-semibold ${tab === value ? 'bg-primary [color:var(--color-on-primary)]' : 'text-text-muted hover:text-text'}`}>
            {label}
          </button>
        ))}
      </div>

      {error ? <div role="alert" className="mb-5 flex gap-2 rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-600"><AlertCircle className="h-5 w-5 shrink-0" />{error}</div> : null}
      {submittedTicket ? <div className="mb-5 flex gap-2 rounded-2xl border border-primary/30 bg-primary/10 p-4 text-sm text-text"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /><span>Report submitted. Your ticket is <strong>{submittedTicket}</strong>.</span></div> : null}

      {tab === 'new' ? (
        <section className="space-y-5 rounded-3xl border border-border bg-surface p-5 shadow-sm md:p-7">
          <Field label="Category"><select value={category} onChange={(event) => setCategory(event.target.value)} className={inputClass}>{SUPPORT_REPORT_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
          <Field label="Short title"><input value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} className={inputClass} placeholder="What went wrong?" /></Field>
          <Field label="What happened?"><textarea value={description} maxLength={4000} onChange={(event) => setDescription(event.target.value)} className={`${inputClass} min-h-32`} placeholder="Describe what you saw and what you were doing." /></Field>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Steps to reproduce (optional)"><textarea value={stepsToReproduce} maxLength={2000} onChange={(event) => setStepsToReproduce(event.target.value)} className={`${inputClass} min-h-24`} /></Field>
            <Field label="What did you expect? (optional)"><textarea value={expectedBehavior} maxLength={2000} onChange={(event) => setExpectedBehavior(event.target.value)} className={`${inputClass} min-h-24`} /></Field>
          </div>

          <div className="rounded-2xl border border-dashed border-border p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><p className="font-semibold text-text">Screenshots or video</p><p className="text-sm text-text-muted">Up to 3 images (8 MB each) and 1 video (50 MB, 60 seconds). Total 75 MB.</p></div>
              <label className="cursor-pointer rounded-xl bg-primary px-4 py-2 text-sm font-semibold [color:var(--color-on-primary)]">
                Add files
                <input className="sr-only" type="file" multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime" onChange={(event) => { onFiles(Array.from(event.target.files || [])); event.currentTarget.value = ''; }} />
              </label>
            </div>
            {files.length ? <ul className="mt-4 space-y-2">{files.map((file, index) => <li key={`${file.name}-${index}`} className="flex items-center gap-3 rounded-xl bg-background p-3 text-sm text-text">{file.type.startsWith('video/') ? <FileVideo className="h-5 w-5 text-primary" /> : <Image className="h-5 w-5 text-primary" />}<span className="min-w-0 flex-1 truncate">{file.name}</span><span className="text-text-muted">{formatBytes(file.size)}</span><button type="button" onClick={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove ${file.name}`}><X className="h-4 w-4" /></button></li>)}</ul> : null}
            {files.length ? <p className="mt-2 text-right text-xs text-text-muted">Total {formatBytes(attachmentTotal)}</p> : null}
          </div>

          <label className="flex items-start gap-3 text-sm text-text-muted"><input type="checkbox" checked={diagnosticsConsent} onChange={(event) => setDiagnosticsConsent(event.target.checked)} className="mt-1 h-4 w-4 accent-primary" /><span>Include browser, device, network state and this page address. Message contents and passwords are never collected.</span></label>
          <button type="button" disabled={busy} onClick={() => void submit()} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 font-semibold [color:var(--color-on-primary)] disabled:opacity-60">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}{busy ? 'Submitting…' : 'Submit report'}</button>
        </section>
      ) : (
        <section className="rounded-3xl border border-border bg-surface p-5 md:p-7">
          <div className="mb-4 flex items-center justify-between"><h2 className="text-xl font-bold text-text">My reports</h2><button type="button" onClick={() => void loadReports()} className="rounded-xl p-2 text-primary" aria-label="Refresh reports"><RefreshCw className={`h-5 w-5 ${loadingReports ? 'animate-spin' : ''}`} /></button></div>
          {loadingReports && !reports.length ? <div className="flex justify-center py-14"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div> : reports.length === 0 ? <p className="py-12 text-center text-text-muted">You have not submitted a report yet.</p> : <div className="space-y-3">{reports.map((report) => <button key={report.ticketReference} type="button" onClick={() => void openReport(report.ticketReference)} className="flex w-full items-center justify-between gap-4 rounded-2xl border border-border bg-background p-4 text-left hover:border-primary/40"><div className="min-w-0"><p className="font-semibold text-text">{report.title}</p><p className="mt-1 text-sm text-text-muted">{report.ticketReference} · {new Date(report.submittedAt).toLocaleString()}</p></div><Status value={report.status} /></button>)}</div>}
        </section>
      )}

      {selected ? <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 md:items-center md:p-5" onMouseDown={() => setSelected(null)}><article className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-surface p-6 shadow-2xl md:rounded-3xl" onMouseDown={(event) => event.stopPropagation()}><div className="flex items-start justify-between gap-4"><div><p className="text-sm font-semibold text-primary">{selected.ticketReference}</p><h2 className="mt-1 text-2xl font-bold text-text">{selected.title}</h2></div><button type="button" onClick={() => setSelected(null)} aria-label="Close details"><X className="h-6 w-6 text-text-muted" /></button></div><div className="mt-4"><Status value={selected.status} /></div><Detail label="What happened" value={selected.description} /><Detail label="Steps to reproduce" value={selected.stepsToReproduce} /><Detail label="Expected behavior" value={selected.expectedBehavior} />{selected.closureNote ? <Detail label="Resolution" value={selected.closureNote} /> : null}{selected.attachments.length ? <div className="mt-5"><p className="text-sm font-semibold text-text">Attachments</p><div className="mt-2 flex flex-wrap gap-2">{selected.attachments.map((attachment) => attachment.readUrl ? <a key={attachment.attachmentId} href={attachment.readUrl} target="_blank" rel="noreferrer" className="rounded-xl border border-border px-3 py-2 text-sm text-primary">Open {attachment.kind.toLowerCase()}</a> : null)}</div></div> : null}</article></div> : null}
    </div>
  );
}

const inputClass = 'w-full rounded-2xl border border-border bg-background px-4 py-3 text-text outline-none focus:border-primary';
function Field({ label, children }: { label: string; children: React.ReactNode }) { return <label className="block"><span className="mb-2 block text-sm font-semibold text-text">{label}</span>{children}</label>; }
function Detail({ label, value }: { label: string; value?: string | null }) { return value ? <div className="mt-5"><p className="text-sm font-semibold text-text">{label}</p><p className="mt-1 whitespace-pre-wrap text-sm text-text-muted">{value}</p></div> : null; }
function Status({ value }: { value: string }) { return <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${value === 'Closed' ? 'bg-slate-500/15 text-text-muted' : 'bg-primary/15 text-primary'}`}>{value}</span>; }
function formatBytes(value: number) { return value >= 1024 * 1024 ? `${(value / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(value / 1024)} KB`; }
function messageOf(error: unknown, fallback: string) { const value = error as { response?: { data?: { message?: string; error?: string } }; message?: string }; return value.response?.data?.message || value.response?.data?.error || value.message || fallback; }
